// Internal goalkeeper match metrics for analytics + coach tooling.
// These helpers are intentionally conservative: if any required inputs are missing
// (or inconsistent), metrics are returned as `null` so the UI can show unavailable.

export const normalizeShotPoints = (value) => {
	if (Array.isArray(value)) {
		return value;
	}
	if (typeof value === "string") {
		try {
			const parsed = JSON.parse(value);
			return Array.isArray(parsed) ? parsed : [];
		} catch (error) {
			return [];
		}
	}
	return [];
};

const toIntOrNull = (value) => {
	if (value === undefined || value === null || value === "") return null;
	const parsed = parseInt(value, 10);
	return Number.isFinite(parsed) ? parsed : null;
};

const toFloatOrNull = (value) => {
	if (value === undefined || value === null || value === "") return null;
	const parsed = parseFloat(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const getPointOutcome = (point) => {
	// Backward-compatible outcome detection for ShotTargetMap points.
	// - legacy: {x, y} -> no outcome
	// - optional: {x, y, outcome/type/result} -> outcome
	const raw = point?.outcome ?? point?.type ?? point?.result;
	if (!raw || typeof raw !== "string") return null;
	const value = raw.toLowerCase();
	if (["goal", "goals", "scored"].includes(value)) return "goal";
	if (["save", "saves", "saved"].includes(value)) return "save";
	if (["shot", "shots"].includes(value)) return "shot";
	return null;
};

const sumIfAllPresentOrNull = (values) => {
	// Formula helper: sum only if all required values are present.
	// If any value is null -> metric is unreliable -> return null.
	if (values.some((v) => v === null)) return null;
	return values.reduce((acc, v) => acc + (v ?? 0), 0);
};

export const computeGoalkeeperMatchMetrics = (reportData) => {
	const shotsOnTargetPoints = normalizeShotPoints(reportData?.shotsOnTarget);
	const shotsOnTargetCount = shotsOnTargetPoints.filter((point) =>
		["goal", "save"].includes(getPointOutcome(point)),
	).length;

	const goalsConcededValue = toIntOrNull(reportData?.totalGoalsConceded);
	const minutesPlayedValue = toIntOrNull(reportData?.minutesPlayed);
	const finalRatingValue = toFloatOrNull(reportData?.generalRating);

	// Per-shot outcome counts (only available if points include outcome metadata).
	const pointOutcomeCounts = shotsOnTargetPoints.reduce(
		(acc, point) => {
			const outcome = getPointOutcome(point);
			if (outcome === "goal") acc.goal += 1;
			else if (outcome === "save") acc.save += 1;
			else if (outcome === "shot") acc.shot += 1;
			return acc;
		},
		{goal: 0, save: 0, shot: 0},
	);

	const hasPerShotOutcomes =
		(pointOutcomeCounts.goal ?? 0) + (pointOutcomeCounts.save ?? 0) > 0;

	// Shot stopping:
	// 1) If per-shot outcomes exist: saves = savesCount, shots = shotsOnTargetCount.
	// 2) Otherwise, derive saves from totals guarded by consistency.
	let savesOnTargetValue = null;
	if (hasPerShotOutcomes) {
		// Formula: saves = number of points marked as "save".
		savesOnTargetValue = pointOutcomeCounts.save;
	} else if (
		goalsConcededValue !== null &&
		shotsOnTargetCount > 0 &&
		goalsConcededValue <= shotsOnTargetCount
	) {
		// Formula: saves = shotsOnTargetCount - goalsConcededValue.
		// Reliability guard: only compute when goalsConcededValue is not greater than shots faced.
		const derived = shotsOnTargetCount - goalsConcededValue;
		savesOnTargetValue = derived >= 0 ? derived : null;
	}

	// Formula: Shot stopping index = save percentage = saves / shots faced.
	const shotStoppingIndexRatio =
		savesOnTargetValue !== null && shotsOnTargetCount > 0
			? savesOnTargetValue / shotsOnTargetCount
			: null;

	// Distribution efficiency:
	// Formula: ok / (ok + failed) using the offensive OK/failed fields.
	const shortPassOk = toIntOrNull(reportData?.shortPassOk);
	const shortPassFailed = toIntOrNull(reportData?.shortPassFailed);
	const longMidThrowOk = toIntOrNull(reportData?.longMidThrowOk);
	const longMidThrowFailed = toIntOrNull(reportData?.longMidThrowFailed);
	const restartWithHandsOk = toIntOrNull(reportData?.restartWithHandsOk);
	const restartWithHandsFailed = toIntOrNull(
		reportData?.restartWithHandsFailed,
	);
	const remittalOnTheFlyOk = toIntOrNull(reportData?.remittalOnTheFlyOk);
	const remittalOnTheFlyFailed = toIntOrNull(
		reportData?.remittalOnTheFlyFailed,
	);
	const positiveTransitionOutsideOk = toIntOrNull(
		reportData?.positiveTransitionOutsideOk,
	);
	const positiveTransitionOutsideFailed = toIntOrNull(
		reportData?.positiveTransitionOutsideFailed,
	);

	const offensiveOkTotal = sumIfAllPresentOrNull([
		shortPassOk,
		longMidThrowOk,
		restartWithHandsOk,
		remittalOnTheFlyOk,
		positiveTransitionOutsideOk,
	]);
	const offensiveFailedTotal = sumIfAllPresentOrNull([
		shortPassFailed,
		longMidThrowFailed,
		restartWithHandsFailed,
		remittalOnTheFlyFailed,
		positiveTransitionOutsideFailed,
	]);
	const offensiveTotal =
		offensiveOkTotal !== null && offensiveFailedTotal !== null
			? offensiveOkTotal + offensiveFailedTotal
			: null;

	const distributionEfficiencyRatio =
		offensiveTotal !== null && offensiveTotal > 0
			? offensiveOkTotal / offensiveTotal
			: null;

	// Aerial control:
	// Formula: (high claims ok + high punches ok) / total (claims+punches).
	const highClaimOk = toIntOrNull(reportData?.highClaimOk);
	const highClaimFailed = toIntOrNull(reportData?.highClaimFailed);
	const highPunchOk = toIntOrNull(reportData?.highPunchOk);
	const highPunchFailed = toIntOrNull(reportData?.highPunchFailed);

	const aerialOkTotal = sumIfAllPresentOrNull([highClaimOk, highPunchOk]);
	const aerialFailedTotal = sumIfAllPresentOrNull([
		highClaimFailed,
		highPunchFailed,
	]);
	const aerialTotal =
		aerialOkTotal !== null && aerialFailedTotal !== null
			? aerialOkTotal + aerialFailedTotal
			: null;

	const aerialControlRatio =
		aerialTotal !== null && aerialTotal > 0
			? aerialOkTotal / aerialTotal
			: null;

	// Set piece control (corners only):
	// Prefer corner event fields only (out/in) to avoid mixing ambiguous aggregates.
	const cornerOutIntervention = toIntOrNull(reportData?.cornerOutIntervention);
	const cornerOutSave = toIntOrNull(reportData?.cornerOutSave);
	const cornerOutDefendedTeammate = toIntOrNull(
		reportData?.cornerOutDefendedTeammate,
	);
	const cornerOutOpponents = toIntOrNull(reportData?.cornerOutOpponents);
	const cornerOutGoal = toIntOrNull(reportData?.cornerOutGoal);

	const cornerRetIntervention = toIntOrNull(reportData?.cornerRetIntervention);
	const cornerRetSave = toIntOrNull(reportData?.cornerRetSave);
	const cornerRetDefendedTeammate = toIntOrNull(
		reportData?.cornerRetDefendedTeammate,
	);
	const cornerRetOpponents = toIntOrNull(reportData?.cornerRetOpponents);
	const cornerRetGoal = toIntOrNull(reportData?.cornerRetGoal);

	const cornerOutTotal = sumIfAllPresentOrNull([
		cornerOutIntervention,
		cornerOutSave,
		cornerOutDefendedTeammate,
		cornerOutOpponents,
		cornerOutGoal,
	]);
	const cornerRetTotal = sumIfAllPresentOrNull([
		cornerRetIntervention,
		cornerRetSave,
		cornerRetDefendedTeammate,
		cornerRetOpponents,
		cornerRetGoal,
	]);
	const setPieceTotal =
		cornerOutTotal !== null && cornerRetTotal !== null
			? cornerOutTotal + cornerRetTotal
			: null;

	const setPieceGoalsTotal =
		cornerOutGoal !== null && cornerRetGoal !== null
			? cornerOutGoal + cornerRetGoal
			: null;

	// Formula: set piece control ratio = (total - goals) / total.
	const setPieceControlRatio =
		setPieceTotal !== null &&
		setPieceTotal > 0 &&
		setPieceGoalsTotal !== null &&
		setPieceGoalsTotal <= setPieceTotal
			? (setPieceTotal - setPieceGoalsTotal) / setPieceTotal
			: null;

	// Error impact:
	// Formula: goals conceded per shot on target faced.
	const errorImpactRatio =
		goalsConcededValue !== null &&
		shotsOnTargetCount > 0 &&
		goalsConcededValue <= shotsOnTargetCount
			? goalsConcededValue / shotsOnTargetCount
			: null;

	// Optional reliability index:
	// Computed as availability of the internal indices (no extra assumptions).
	const indices = [
		shotStoppingIndexRatio,
		distributionEfficiencyRatio,
		aerialControlRatio,
		setPieceControlRatio,
		errorImpactRatio,
		finalRatingValue !== null ? finalRatingValue / 10 : null, // availability only, not scoring
	];
	const availableCount = indices.filter((v) => v !== null).length;
	const reliabilityRatio = indices.length
		? availableCount / indices.length
		: null;

	return {
		shotsOnTargetPoints,
		shotsOnTargetCount,
		hasPerShotOutcomes,
		pointOutcomeCounts,

		minutesPlayedValue,
		goalsConcededValue,
		finalRatingValue,

		savesOnTargetValue,
		shotStoppingIndexRatio,
		distributionEfficiencyRatio,
		aerialControlRatio,
		setPieceControlRatio,
		errorImpactRatio,

		seasonSamples: {
			shotStoppingRatio:
				shotStoppingIndexRatio == null
					? null
					: {successful: savesOnTargetValue, attempted: shotsOnTargetCount},
			distributionRatio:
				distributionEfficiencyRatio == null
					? null
					: {successful: offensiveOkTotal, attempted: offensiveTotal},
			aerialControlRatio:
				aerialControlRatio == null
					? null
					: {successful: aerialOkTotal, attempted: aerialTotal},
			setPieceControlRatio:
				setPieceControlRatio == null
					? null
					: {
							successful: setPieceTotal - setPieceGoalsTotal,
							attempted: setPieceTotal,
						},
			errorImpactRatio:
				errorImpactRatio == null
					? null
					: {successful: goalsConcededValue, attempted: shotsOnTargetCount},
		},
		reliabilityRatio,
	};
};
